import React, { Component } from 'react';

import { includes, uniq, difference, intersection, isEqual } from 'lodash';
import { getLocalValue, setLocalValue } from 'component-utilities/localStorage/localStorage';

import toggleInArray from 'component-lib/toggleInArray';

const GLOBAL_STORE_NAME = 'multiselect';

const DEFAULT_STATE = {
  isAllSelected: false,
  selectedRows: [],
  totalCount: false,
};

const storedValues = getLocalValue(GLOBAL_STORE_NAME);

if (!storedValues) {
  setLocalValue(GLOBAL_STORE_NAME, DEFAULT_STATE);
}

let skipReset = false;

function MultiSelect(WrappedComponent) {
  class MultiSelectComponent extends Component {
    static propTypes = {};

    constructor(props) {
      super(props);

      if (!isEqual(getLocalValue(GLOBAL_STORE_NAME), DEFAULT_STATE)) {
        skipReset = true;
      }

      this.state = { ...getLocalValue(GLOBAL_STORE_NAME) };
    }

    componentWillUnmount() {
      setLocalValue(GLOBAL_STORE_NAME, DEFAULT_STATE);
    }

    onAllClick = (isAllSelected = !this.state.isAllSelected) => {
      this.updateState({
        isAllSelected,
        selectedRows: [],
      });
    };

    onFilterChange = (ids) => {
      const { isAllSelected, selectedRows } = this.state;

      if (isAllSelected) {
        this.updateState({
          isAllSelected: false,
          selectedRows: difference(ids, selectedRows),
        });
      }
    };

    onReturnCountChange = (returnCount, ids) => {
      const { selectedRows } = this.state;
      const idsInSelection = intersection(selectedRows, ids);

      if (idsInSelection.length === returnCount && !difference(idsInSelection, ids).length) {
        this.updateState({
          isAllSelected: true,
          selectedRows: [],
        });
      } else {
        this.updateState({
          isAllSelected: false,
          selectedRows: idsInSelection,
        });
      }
    };

    onRowClick = (rowId) => {
      const { selectedRows, totalCount } = this.state;
      const newSelectedRows = toggleInArray(selectedRows, rowId);

      if (totalCount && newSelectedRows.length === totalCount) {
        this.onAllClick();
      } else {
        this.updateState({
          selectedRows: newSelectedRows,
        });
      }
    };

    setResultCounts = (total) => {
      this.updateState({
        totalCount: total,
      });
    };

    updateState = (newValues) => {
      this.setState({ ...newValues }, () => {
        setLocalValue(GLOBAL_STORE_NAME, { ...this.state });
      });
    };

    isRowSelected = rowId =>
      (this.state.isAllSelected && !includes(this.state.selectedRows, rowId)) ||
      (!this.state.isAllSelected && includes(this.state.selectedRows, rowId));

    reset = () => {
      if (skipReset) {
        skipReset = false;
      } else {
        this.updateState({ ...DEFAULT_STATE });
      }
    };

    apiParams = (allId, loggedInAsUserId) => {
      const { isAllSelected, selectedRows } = this.state;

      return {
        ...(isAllSelected && {
          filter: 'all',
          ids: [],
          except: uniq([...allId.filter(id => includes(selectedRows, id)), loggedInAsUserId]),
        }),
        ...(!isAllSelected && {
          ids: selectedRows,
          except: [loggedInAsUserId],
        }),
      };
    };

    createDSL = (query) => {
      const { isAllSelected, selectedRows } = this.state;

      const appendToQuery = multiSelectQuery => `(and ${query} ${multiSelectQuery})`;
      const mapAssetIds = id => `(equal id "${id}")`;
      const assetIDQuery = ids => `(or ${ids.map(mapAssetIds).join(' ')})`;
      const multiSelectQuery = (assetIDs, isNot) => {
        const idQuery = assetIDQuery(assetIDs);

        if (isNot) {
          return appendToQuery(`(not ${idQuery})`);
        }
        return appendToQuery(idQuery);
      };

      if (!isAllSelected) {
        return multiSelectQuery(selectedRows, false);
      }

      if (isAllSelected && selectedRows.length) {
        return multiSelectQuery(selectedRows, true);
      }

      return query;
    };

    render() {
      return (
        <WrappedComponent
          {...this.props}
          multiSelect={{
            ...this.state,
            reset: this.reset,
            apiParams: this.apiParams,
            onAllClick: this.onAllClick,
            onFilterChange: this.onFilterChange,
            onReturnCountChange: this.onReturnCountChange,
            onRowClick: this.onRowClick,
            isRowSelected: this.isRowSelected,
            createDSL: this.createDSL,
            setResultCounts: this.setResultCounts,
          }}
        />
      );
    }
  }

  return MultiSelectComponent;
}

export default MultiSelect;
